import { normalizeIndex } from '../reducerUtilities/normalizeMealIdeaIndex';

/**
 * The food store's entities (docs/local-first-food-data.md, phase 2) from the server's payloads:
 * plain objects keyed for lookup, so the same object is persisted and rendered.
 *
 * - groceryItems, mealIdeas, mealIdeaGroups, calendarEvents, groceryRows: by id
 * - groupOrder: group ids in display order, as normalizeIndex() gives them
 * - groceryLists: by key, "active" or "bought:<Y-m-d>", so a row can move onto a day's list the
 *   server has not made yet
 * - meals: by mealKey(); slotNotes: by "<Y-m-d>|<type>"
 * - lastBoughtByItem, lastBoughtByName: grocery item id, or lowercased name, to 'Y-m-d'
 * - lastBoughtByUnlinkedName: the same by name, over only bought rows that name no grocery item
 *
 * Every function here returns new objects and leaves its arguments alone.
 */

export const ACTIVE_LIST_KEY = 'active';

export const emptyEntities = () => ({
	groceryItems: {},
	mealIdeas: {},
	mealIdeaGroups: {},
	groupOrder: [],
	groceryLists: {},
	groceryRows: {},
	lastBoughtByItem: {},
	lastBoughtByName: {},
	lastBoughtByUnlinkedName: {},
	meals: {},
	slotNotes: {},
	calendarEvents: {},
});

const pad = (number) => String(number).padStart(2, '0');

/** 'Y-m-d' from year, month counted from 1, and day. */
export const ymd = (year, month, day) => `${year}-${pad(month)}-${pad(day)}`;

/** A meal's or slot note's day: its `date`, or the year/month/day the legacy endpoints send. */
export const dateOf = (entity) => entity.date || ymd(entity.year, entity.month, entity.day);

/**
 * What a meal is keyed by: its uid, which the browser mints for a meal it makes (phase 4), so a meal
 * made offline needs no new key once the server has it. A meal without one is keyed by its id.
 */
export const mealKey = (meal) => (meal.uid ? String(meal.uid) : `id:${meal.id}`);

export const slotNoteKey = (note) => `${dateOf(note)}|${Number(note.type) || 0}`;

/** A list's key: the one the server sent, or the same rule Food_Serializer::list_key() follows. */
export const listKeyOf = (list) => {
	if(list.key) {
		return list.key;
	}
	if(list.is_active === true || list.is_active === 1 || list.is_active === '1') {
		return ACTIVE_LIST_KEY;
	}
	if(list.purchased_on) {
		return `bought:${list.purchased_on}`;
	}
	return `list:${list.id}`;
};

const listOf = (list) => {
	const { items, dates, ...fields } = list;
	return { ...fields, key: listKeyOf(list) };
};

const mealOf = (meal) => ({ ...meal, date: dateOf(meal) });

const noteOf = (note) => ({ ...note, date: dateOf(note), type: Number(note.type) || 0 });

const byId = (entities) => {
	const map = {};
	(entities || []).forEach((entity) => {
		map[Number(entity.id)] = entity;
	});
	return map;
};

const inDays = (date, from, to) => !!date && date >= from && date <= to;

const localDayStart = (date) => {
	const [year, month, day] = date.split('-').map(Number);
	return new Date(year, month - 1, day).getTime() / 1000;
};

/** Whether the event, unix seconds, overlaps the days `from` through `to`, in the browser's time. */
export const eventOverlaps = (event, from, to) => {
	const start = Number(event.start_time);
	if(!event.start_time && event.start_time !== 0) {
		return false;
	}
	const end = event.end_time === null || event.end_time === undefined ? start : Number(event.end_time);
	return start < localDayStart(to) + 86400 && end >= localDayStart(from);
};

/** Group ids by the order normalizeIndex() puts them in. */
const groupOrderOf = (groupsById) => Object.values(groupsById)
	.sort((a, b) => (a.order - b.order) || String(a.name).localeCompare(String(b.name)))
	.map((group) => group.id);

/** Each group keeps only ideas that exist, as normalizeIndex() does. */
const withLiveMembers = (groupsById, ideasById) => {
	const groups = {};
	Object.values(groupsById).forEach((group) => {
		const members = group.meal_idea_ids.filter((id) => ideasById[id]);
		groups[group.id] = members.length === group.meal_idea_ids.length ? group : { ...group, meal_idea_ids: members };
	});
	return groups;
};

/** Ideas and groups from meal_idea/index's shape. */
const ideasOf = (ideas, groups) => {
	const normalized = normalizeIndex({ ideas: ideas || [], groups: groups || [] });
	return { mealIdeas: normalized.ideasById, mealIdeaGroups: normalized.groupsById, groupOrder: normalized.groupOrder };
};

const rowsOf = (rows) => {
	const map = {};
	(rows || []).forEach((row) => {
		map[Number(row.id)] = row;
	});
	return map;
};

const without = (map, drop) => {
	const kept = {};
	Object.keys(map).forEach((key) => {
		if(!drop(map[key], key)) {
			kept[key] = map[key];
		}
	});
	return kept;
};

/** The entities of a payload's lists and rows, keyed. */
const listsAndRows = (payload) => {
	const groceryLists = {};
	(payload.grocery_lists || []).forEach((list) => {
		const normalized = listOf(list);
		groceryLists[normalized.key] = normalized;
	});
	return { groceryLists, groceryRows: rowsOf(payload.grocery_rows) };
};

/** Meals, slot notes and events of a payload, keyed. */
const calendarOf = (payload) => {
	const meals = {};
	(payload.meals || []).forEach((meal) => {
		const normalized = mealOf(meal);
		meals[mealKey(normalized)] = normalized;
	});
	const slotNotes = {};
	(payload.slot_notes || []).forEach((note) => {
		const normalized = noteOf(note);
		slotNotes[slotNoteKey(normalized)] = normalized;
	});
	return { meals, slotNotes, calendarEvents: byId(payload.calendar_events) };
};

/**
 * Replaces the days `from` through `to` with `payload`'s meals, slot notes, events and the bought
 * lists dated in them, and replaces any other list `alsoReplacing(list, key)` names. A row on a
 * replaced list goes with it unless the payload has it.
 */
const replaceDays = (entities, from, to, payload, alsoReplacing = () => false) => {
	const incoming = listsAndRows(payload);
	const replaced = (list, key) => !!incoming.groceryLists[key] || inDays(list.purchased_on, from, to) || alsoReplacing(list, key);
	const groceryLists = { ...without(entities.groceryLists, replaced), ...incoming.groceryLists };
	const groceryRows = {
		...without(entities.groceryRows, (row) => !groceryLists[row.list_key] || replaced(entities.groceryLists[row.list_key] || {}, row.list_key)),
		...incoming.groceryRows,
	};
	const calendar = calendarOf(payload);
	return {
		...entities,
		groceryLists,
		groceryRows,
		meals: { ...without(entities.meals, (meal) => inDays(meal.date, from, to)), ...calendar.meals },
		slotNotes: { ...without(entities.slotNotes, (note) => inDays(note.date, from, to)), ...calendar.slotNotes },
		calendarEvents: { ...without(entities.calendarEvents, (event) => eventOverlaps(event, from, to)), ...calendar.calendarEvents },
	};
};

/**
 * A snapshot replaces only what it is authoritative for: the catalog, the ideas and groups, the
 * active list, the bought lists it returned or that are dated in its window, the last-bought maps,
 * and the meals, slot notes and events in its window. Lists and days loaded through food/range or
 * food/bought_lists outside that are kept.
 */
export const applySnapshot = (entities, snapshot) => {
	const { from, to } = snapshot.window;
	const replaced = replaceDays(entities, from, to, snapshot, (list, key) => key === ACTIVE_LIST_KEY);
	return {
		...replaced,
		groceryItems: byId(snapshot.grocery_items),
		...ideasOf(snapshot.meal_ideas, snapshot.meal_idea_groups),
		lastBoughtByItem: { ...(snapshot.last_bought_by_item || {}) },
		lastBoughtByName: { ...(snapshot.last_bought_by_name || {}) },
		lastBoughtByUnlinkedName: { ...(snapshot.last_bought_by_unlinked_name || {}) },
	};
};

/** food/range: whole months, `from` through `to`, replaced. */
export const applyRange = (entities, from, to, payload) => replaceDays(entities, from, to, payload);

/** A page of food/bought_lists: those lists, each with every row it has, merged in. */
export const applyBoughtLists = (entities, payload) => {
	const incoming = listsAndRows(payload);
	return {
		...entities,
		groceryLists: { ...entities.groceryLists, ...incoming.groceryLists },
		groceryRows: { ...without(entities.groceryRows, (row) => !!incoming.groceryLists[row.list_key]), ...incoming.groceryRows },
	};
};

/** A last-bought map with `days` merged in: a null day means never bought, and drops the key. */
const mergeDays = (map, days) => {
	const merged = { ...map };
	Object.keys(days).forEach((key) => {
		if(days[key] === null || days[key] === undefined) {
			delete merged[key];
		} else {
			merged[key] = days[key];
		}
	});
	return merged;
};

/**
 * Merges a `changes` (see the design's "Actions"): every key optional; a slot note with empty notes,
 * an event marked deleted, or a last-bought day of null is a delete; deleting a list takes the rows
 * still on it.
 */
export const applyChanges = (entities, changes) => {
	const upsert = changes.upsert || {};
	const remove = changes.delete || {};
	const next = { ...entities };

	if(upsert.grocery_lists || remove.grocery_lists) {
		const removed = new Set(remove.grocery_lists || []);
		next.groceryLists = { ...without(entities.groceryLists, (list, key) => removed.has(key)) };
		(upsert.grocery_lists || []).forEach((list) => {
			const normalized = listOf(list);
			next.groceryLists[normalized.key] = normalized;
		});
		if(removed.size) {
			next.groceryRows = without(entities.groceryRows, (row) => removed.has(row.list_key) && !next.groceryLists[row.list_key]);
		}
	}
	if(upsert.grocery_rows || remove.grocery_rows) {
		const removed = new Set((remove.grocery_rows || []).map(Number));
		next.groceryRows = { ...without(next.groceryRows, (row, id) => removed.has(Number(id))), ...rowsOf(upsert.grocery_rows) };
	}
	if(upsert.meals || remove.meals) {
		const removed = new Set(remove.meals || []);
		next.meals = { ...without(entities.meals, (meal, key) => removed.has(key)) };
		(upsert.meals || []).forEach((meal) => {
			const normalized = mealOf(meal);
			next.meals[mealKey(normalized)] = normalized;
		});
	}
	if(upsert.slot_notes || remove.slot_notes) {
		const removed = new Set(remove.slot_notes || []);
		next.slotNotes = { ...without(entities.slotNotes, (note, key) => removed.has(key)) };
		(upsert.slot_notes || []).forEach((note) => {
			const normalized = noteOf(note);
			if(normalized.notes) {
				next.slotNotes[slotNoteKey(normalized)] = normalized;
			} else {
				delete next.slotNotes[slotNoteKey(normalized)];
			}
		});
	}
	if(upsert.calendar_events || remove.calendar_events) {
		const removed = new Set((remove.calendar_events || []).map(Number));
		next.calendarEvents = { ...without(entities.calendarEvents, (event, id) => removed.has(Number(id))) };
		(upsert.calendar_events || []).forEach((event) => {
			if(event.deleted) {
				delete next.calendarEvents[Number(event.id)];
			} else {
				next.calendarEvents[Number(event.id)] = event;
			}
		});
	}
	if(upsert.grocery_items || remove.grocery_items) {
		const removed = new Set((remove.grocery_items || []).map(Number));
		next.groceryItems = { ...without(entities.groceryItems, (item, id) => removed.has(Number(id))), ...byId(upsert.grocery_items) };
	}
	if(upsert.meal_ideas || remove.meal_ideas || upsert.meal_idea_groups || remove.meal_idea_groups) {
		const removedIdeas = new Set((remove.meal_ideas || []).map(Number));
		const removedGroups = new Set((remove.meal_idea_groups || []).map(Number));
		const incoming = ideasOf(upsert.meal_ideas, []);
		const mealIdeas = { ...without(entities.mealIdeas, (idea, id) => removedIdeas.has(Number(id))), ...incoming.mealIdeas };
		// Normalized against every idea, not only the ones in this change.
		const incomingGroups = normalizeIndex({ ideas: Object.values(mealIdeas), groups: upsert.meal_idea_groups || [] }).groupsById;
		const mealIdeaGroups = withLiveMembers({
			...without(entities.mealIdeaGroups, (group, id) => removedGroups.has(Number(id))),
			...incomingGroups,
		}, mealIdeas);
		next.mealIdeas = mealIdeas;
		next.mealIdeaGroups = mealIdeaGroups;
		next.groupOrder = groupOrderOf(mealIdeaGroups);
	}
	if(upsert.last_bought_by_item) {
		next.lastBoughtByItem = mergeDays(entities.lastBoughtByItem, upsert.last_bought_by_item);
	}
	if(upsert.last_bought_by_name) {
		next.lastBoughtByName = mergeDays(entities.lastBoughtByName, upsert.last_bought_by_name);
	}
	return next;
};

/**
 * The list key of the list with `id` among `entities`, or of one of `lists` (server shapes), or
 * null. For legacy answers, which name lists by id.
 */
export const listKeyForId = (entities, id, lists = []) => {
	const numeric = Number(id);
	const answered = lists.find((list) => Number(list.id) === numeric);
	if(answered) {
		return listKeyOf(answered);
	}
	const known = Object.values(entities.groceryLists).find((list) => list.id !== null && Number(list.id) === numeric);
	return known ? known.key : null;
};
